'use client';

import {
  Badge,
  Icons,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@commenda/kakashi';
import { AlertCircle } from 'lucide-react';
import type { CommendaOSFilter, ContextBinding } from '@commenda/types';
import {
  ContextSourceField,
  FORM_CONTEXT_MARKER,
} from './context-helpers';

// Filter Configuration Editor - Configure filters for relation entities
export function FilterConfigEditor({
  entitySchema,
  filters,
  onChange,
}: {
  entitySchema: {
    filters?: Record<
      string,
      {
        type: string;
        label: string;
        enum?: string;
        enumOptions?: Array<{ value: string; label: string }>;
        description?: string;
      }
    >;
  };
  filters?: CommendaOSFilter[];
  onChange: (filters: CommendaOSFilter[] | undefined) => void;
}) {
  if (!entitySchema.filters) {
    return null;
  }

  const availableFilters = Object.entries(entitySchema.filters);

  const handleFilterChange = (key: string, value: string | number) => {
    const otherFilters = filters?.filter((f) => f.key !== key) || [];
    if (value === '' || value === undefined) {
      onChange(otherFilters.length > 0 ? otherFilters : undefined);
    } else {
      onChange([...otherFilters, { key, value }]);
    }
  };

  const getFilterValue = (key: string): string | number | undefined =>
    filters?.find((f) => f.key === key)?.value;

  return (
    <div className="space-y-3">
      <Label>Filters</Label>
      <p className="txt-xs-regular text-text-tertiary">
        Configure filters to target a specific item in the collection.
      </p>
      <div className="space-y-2 pl-2 border-l border-border-secondary">
        {availableFilters.map(([key, filterDef]) => (
          <div key={key} className="space-y-1">
            <Label className="txt-xs-medium">{filterDef.label}</Label>
            {filterDef.type === 'enum' && filterDef.enumOptions ? (
              <Select
                value={String(getFilterValue(key) ?? '')}
                onValueChange={(value) => handleFilterChange(key, value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={`Select ${filterDef.label}...`} />
                </SelectTrigger>
                <SelectContent>
                  {filterDef.enumOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                value={String(getFilterValue(key) ?? '')}
                onChange={(e) => handleFilterChange(key, e.target.value)}
                placeholder={
                  filterDef.description || `Enter ${filterDef.label}...`
                }
              />
            )}
            {filterDef.description && (
              <p className="txt-xs-regular text-text-tertiary">
                {filterDef.description}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// Select source for context values (one section per required context key)
export function ContextBindingEditor({
  entity,
  contextKeys,
  contextBindings,
  availableFields,
  formContextKeys,
  onChange,
}: {
  entity: string;
  /** Required context keys for this entity (e.g. ['corporationId', 'directorId'] for keyPersonRelationship) */
  contextKeys: string[];
  contextBindings?: ContextBinding[];
  availableFields: ContextSourceField[];
  /** Available context keys from form settings (e.g., ['companyId', 'corporationId']) */
  formContextKeys: string[];
  onChange: (bindings: ContextBinding[] | undefined) => void;
}) {
  // Sort fields: EntitySelect fields that output the target entity first
  const sortedFields = [...availableFields].sort((a, b) => {
    if (a.outputsEntityId && !b.outputsEntityId) return -1;
    if (!a.outputsEntityId && b.outputsEntityId) return 1;
    return 0;
  });

  return (
    <div className="space-y-4">
      {contextKeys.map((contextKey) => {
        const currentBinding = contextBindings?.find(
          (b) => b.contextKey === contextKey
        );
        const useFieldBinding =
          currentBinding?.sourceFieldId &&
          currentBinding.sourceFieldId !== FORM_CONTEXT_MARKER;
        const relevantFormContextKeys = formContextKeys.filter(
          (k) => k === contextKey
        );
        const isFormContextAvailable = relevantFormContextKeys.length > 0;

        const handleSelectFormContext = () => {
          const otherBindings =
            contextBindings?.filter((b) => b.contextKey !== contextKey) || [];
          onChange([
            ...otherBindings,
            { contextKey, sourceFieldId: FORM_CONTEXT_MARKER },
          ]);
        };

        const handleSelectFieldBinding = () => {
          const defaultField =
            sortedFields.find((f) => f.outputsEntityId) || sortedFields[0];
          if (defaultField) {
            const otherBindings =
              contextBindings?.filter((b) => b.contextKey !== contextKey) || [];
            onChange([
              ...otherBindings,
              { contextKey, sourceFieldId: defaultField.fieldId },
            ]);
          }
        };

        const handleFieldChange = (fieldId: string) => {
          const otherBindings =
            contextBindings?.filter((b) => b.contextKey !== contextKey) || [];
          onChange([...otherBindings, { contextKey, sourceFieldId: fieldId }]);
        };

        const selectedField = availableFields.find(
          (f) => f.fieldId === currentBinding?.sourceFieldId
        );

        return (
          <div key={contextKey} className="space-y-3">
            <Label>Context Source ({contextKey})</Label>
            <div className="space-y-2">
              {/* Form Context Option */}
              <div
                className={`flex flex-col gap-2 p-2 rounded-md cursor-pointer border ${
                  !useFieldBinding
                    ? 'border-border-brand bg-bg-brand-subtle'
                    : 'border-border-secondary hover:bg-bg-secondary'
                }`}
                onClick={handleSelectFormContext}
              >
                <div className="flex items-center gap-2">
                  <div
                    className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                      !useFieldBinding
                        ? 'border-border-brand'
                        : 'border-border-tertiary'
                    }`}
                  >
                    {!useFieldBinding && (
                      <div className="w-2 h-2 rounded-full bg-bg-brand" />
                    )}
                  </div>
                  <span className="txt-small-regular text-text-secondary">
                    Use form context
                  </span>
                </div>

                {!useFieldBinding && (
                  <div className="ml-6 space-y-2">
                    {isFormContextAvailable ? (
                      <div className="p-2 bg-bg-success-subtle rounded-md">
                        <p className="txt-xs-regular text-text-success">
                          <Icons.CheckCircle className="inline-block w-3 h-3 mr-1" />
                          Will use{' '}
                          <code className="bg-bg-tertiary px-1 py-0.5 rounded">
                            {contextKey}
                          </code>{' '}
                          from form context
                        </p>
                      </div>
                    ) : (
                      <div className="p-2 bg-bg-warning-subtle rounded-md">
                        <p className="txt-xs-regular text-text-warning">
                          <AlertCircle className="inline-block w-3 h-3 mr-1" />
                          <code className="bg-bg-tertiary px-1 py-0.5 rounded">
                            {contextKey}
                          </code>{' '}
                          is not configured in Form Settings. Enable it in Form
                          Settings &gt; Context Requirements.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Field Binding Option */}
              <div
                className={`flex flex-col gap-2 p-2 rounded-md cursor-pointer border ${
                  useFieldBinding
                    ? 'border-border-brand bg-bg-brand-subtle'
                    : 'border-border-secondary hover:bg-bg-secondary'
                }`}
                onClick={() => !useFieldBinding && handleSelectFieldBinding()}
              >
                <div className="flex items-center gap-2">
                  <div
                    className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                      useFieldBinding
                        ? 'border-border-brand'
                        : 'border-border-tertiary'
                    }`}
                  >
                    {useFieldBinding && (
                      <div className="w-2 h-2 rounded-full bg-bg-brand" />
                    )}
                  </div>
                  <span className="txt-small-regular text-text-secondary">
                    Use value from previous field
                  </span>
                </div>

                {useFieldBinding && (
                  <div className="ml-6 space-y-2">
                    {availableFields.length > 0 ? (
                      <>
                        <Select
                          value={currentBinding?.sourceFieldId || ''}
                          onValueChange={handleFieldChange}
                        >
                          <SelectTrigger onClick={(e) => e.stopPropagation()}>
                            <SelectValue placeholder="Select source field" />
                          </SelectTrigger>
                          <SelectContent>
                            {sortedFields.map((field) => (
                              <SelectItem
                                key={field.fieldId}
                                value={field.fieldId}
                              >
                                <div className="flex items-center gap-2">
                                  <span>{field.label}</span>
                                  {field.outputsEntityId && (
                                    <Badge variant="gray" className="text-xs">
                                      {entity} ID
                                    </Badge>
                                  )}
                                  {!field.outputsEntityId &&
                                    field.entityType && (
                                      <Badge variant="gray" className="text-xs">
                                        {field.entityType}
                                      </Badge>
                                    )}
                                  {!field.outputsEntityId &&
                                    !field.entityType && (
                                      <span className="text-xs text-text-tertiary">
                                        ({field.component})
                                      </span>
                                    )}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        {selectedField?.outputsEntityId && (
                          <div className="p-2 bg-bg-success-subtle rounded-md">
                            <p className="txt-xs-regular text-text-success">
                              <Icons.CheckCircle className="inline-block w-3 h-3 mr-1" />
                              This field outputs {entity} IDs - perfect match!
                            </p>
                          </div>
                        )}

                        {selectedField && !selectedField.outputsEntityId && (
                          <div className="p-2 bg-bg-warning-subtle rounded-md">
                            <p className="txt-xs-regular text-text-warning">
                              <AlertCircle className="inline-block w-3 h-3 mr-1" />
                              Make sure this field contains a valid {entity} ID.
                            </p>
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="txt-xs-regular text-text-tertiary">
                        No previous fields available. Add an EntitySelect field
                        before this one to use dynamic context.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
